import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, X } from "lucide-react";
import SignInMenu, { UserChip } from "./SignInMenu";
import { useAuth } from "../lib/useAuth";
import { clearAuth } from "../lib/auth";

/**
 * Global site header — sticky, 68px, PRD colours, backdrop-blurred.
 *
 * Layouts (owner directive 2026-02-01, FIX 2):
 *   Desktop (md+): wordmark · [inline search form] · leaderboard/admin/user (unchanged)
 *   Mobile      : wordmark · [search icon] · [hamburger]
 *                 search icon expands into a full-width input row below
 *                 the header; hamburger opens an overlay panel with
 *                 Leaderboard / My Entries / Profile / Admin / Sign in|out.
 *
 * Search input performance (owner directive 2026-02-01, FIX 3A):
 *   Both inputs hold their own local state; the mobile input debounces
 *   at 300ms before navigating to `/search?q=<val>`. Neither input
 *   fires a request per keystroke.
 */
export default function Header() {
  const [q, setQ] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();
  const auth = useAuth();

  function onSubmit(e) {
    e.preventDefault();
    const trimmed = q.trim();
    if (!trimmed) return;
    nav(`/search?q=${encodeURIComponent(trimmed)}&page=1`);
    setMobileSearchOpen(false);
  }

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 h-header w-full border-b border-ink-chip/70 bg-ink-bg/85 backdrop-blur-[14px]"
      >
        <div className="mx-auto flex h-full w-full max-w-[1400px] items-center gap-4 px-5 md:gap-8 md:px-gutter">
          <Link
            to="/"
            data-testid="site-wordmark"
            aria-label="CardCapture — home"
            className="inline-flex items-center"
          >
            <img
              src="/brand/logo-reversed.svg"
              alt="CardCapture"
              data-testid="site-logo"
              width="150"
              height="32"
              className="h-8 w-auto select-none"
            />
          </Link>

          {/* Desktop inline search — unchanged from prior owner-signed-off shape. */}
          <form
            onSubmit={onSubmit}
            role="search"
            data-testid="global-search-form"
            className="hidden flex-1 md:flex md:max-w-[560px]"
          >
            <label className="flex w-full items-center gap-2 rounded-card border border-ink-chip bg-ink-surface px-3 py-2 text-body focus-within:border-indigo-text">
              <Search size={16} className="text-ink-muted" />
              <input
                data-testid="global-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="search"
                placeholder="Search cards by name"
                className="w-full bg-transparent text-ink-text placeholder:text-ink-muted focus:outline-none"
                aria-label="Search cards by name"
              />
            </label>
          </form>

          <div className="ml-auto flex items-center gap-3">
            <Link
              data-testid="header-leaderboard-link"
              to="/leaderboard"
              className="hidden text-meta text-ink-muted transition-colors duration-cc ease-cc-ease hover:text-ink-text md:inline"
            >
              Leaderboard
            </Link>
            {auth.user?.is_admin && (
              <Link
                data-testid="header-admin-link"
                to="/admin"
                className="hidden text-meta text-ink-muted transition-colors duration-cc ease-cc-ease hover:text-ink-text md:inline"
              >
                Admin
              </Link>
            )}

            {/* Desktop user/sign-in chip. */}
            <div className="hidden md:block">
              {auth.user ? (
                <UserChip user={auth.user} onSignOut={() => clearAuth()} />
              ) : (
                <SignInMenu />
              )}
            </div>

            {/* Mobile-only icons. */}
            <button
              type="button"
              data-testid="mobile-search-toggle"
              aria-label={mobileSearchOpen ? "Close search" : "Open search"}
              aria-expanded={mobileSearchOpen}
              onClick={() => {
                setMenuOpen(false);
                setMobileSearchOpen((v) => !v);
              }}
              className="inline-flex h-9 w-9 items-center justify-center rounded-chip border border-ink-chip text-ink-muted transition-colors duration-cc ease-cc-ease hover:border-ink-muted hover:text-ink-text md:hidden"
            >
              {mobileSearchOpen ? <X size={18} /> : <Search size={18} />}
            </button>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => {
                setMobileSearchOpen(false);
                setMenuOpen((v) => !v);
              }}
              className="inline-flex h-9 w-9 items-center justify-center rounded-chip border border-ink-chip text-ink-muted transition-colors duration-cc ease-cc-ease hover:border-ink-muted hover:text-ink-text md:hidden"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {mobileSearchOpen && (
          <MobileSearchRow
            onSubmit={onSubmit}
            initialValue={q}
            onValueChange={setQ}
            onClose={() => setMobileSearchOpen(false)}
          />
        )}
      </header>

      {menuOpen && (
        <MobileMenuPanel
          user={auth.user}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Full-width search row that drops below the sticky header on mobile.
 * Holds its own local input state so keystrokes stay fluid; a
 * debounced copy of the value fires a navigation to `/search?q=...`
 * after 300ms of quiescence. Submitting (Enter) skips the debounce.
 */
function MobileSearchRow({ onSubmit, initialValue, onValueChange, onClose }) {
  const [localValue, setLocalValue] = useState(initialValue);
  const nav = useNavigate();
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Autofocus once when the row opens.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Debounced navigation on typing. Held inline (no external hook) so
  // the timer / cleanup ownership is unambiguous — the previous
  // useDebouncedValue extraction interacted badly with React 18's
  // concurrent scheduling and dev-mode strict double-invoke, and the
  // navigate never fired.
  function onChange(nextValue) {
    setLocalValue(nextValue);
    onValueChange(nextValue);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const trimmed = nextValue.trim();
    if (!trimmed) return;
    timerRef.current = setTimeout(() => {
      nav(`/search?q=${encodeURIComponent(trimmed)}&page=1`);
      timerRef.current = null;
    }, 300);
  }

  // Clear any pending debounce on unmount so we don't navigate after
  // the row has been closed.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return (
    <div
      data-testid="mobile-search-row"
      className="border-t border-ink-chip/70 bg-ink-bg px-5 py-3 md:hidden"
    >
      <form onSubmit={onSubmit} role="search">
        <label className="flex w-full items-center gap-2 rounded-card border border-ink-chip bg-ink-surface px-3 py-2 text-body focus-within:border-indigo-text">
          <Search size={16} className="text-ink-muted" />
          <input
            ref={inputRef}
            data-testid="mobile-search-input"
            value={localValue}
            onChange={(e) => onChange(e.target.value)}
            type="search"
            enterKeyHint="search"
            placeholder="Search cards, sets, or numbers"
            className="w-full bg-transparent text-ink-text placeholder:text-ink-muted focus:outline-none"
            aria-label="Search"
          />
        </label>
      </form>
    </div>
  );
}

/**
 * Mobile-only overlay panel with the nav items that live on the
 * desktop header. Rendered as a portal-adjacent sibling of the
 * header so it can cover the full viewport height.
 */
function MobileMenuPanel({ user, onClose }) {
  const linkClass =
    "block rounded-card border border-ink-chip bg-ink-surface px-4 py-3 text-body text-ink-text transition-colors duration-cc ease-cc-ease hover:border-ink-muted";
  const mutedClass = "text-meta text-ink-muted uppercase tracking-wide";

  return (
    <div
      data-testid="mobile-menu-panel"
      className="fixed inset-0 top-[68px] z-30 flex flex-col gap-4 overflow-y-auto bg-ink-bg px-5 py-6 md:hidden"
      role="dialog"
      aria-label="Site navigation"
    >
      {/* Account / auth cluster at the top so the primary CTA is above the fold. */}
      <div className="flex flex-col gap-2" data-testid="mobile-menu-auth">
        {user ? (
          <>
            <div className={mutedClass}>Signed in as</div>
            <div className="text-body text-ink-text truncate" data-testid="mobile-menu-user-email">
              {user.email}
            </div>
            <Link
              to="/profile"
              onClick={onClose}
              data-testid="mobile-menu-profile"
              className={linkClass}
            >
              Profile
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-signout"
              onClick={() => {
                clearAuth();
                onClose();
              }}
              className={`${linkClass} text-left`}
            >
              Sign out
            </button>
          </>
        ) : (
          <>
            <div className={mutedClass}>Account</div>
            <div data-testid="mobile-menu-signin">
              <SignInMenu />
            </div>
          </>
        )}
      </div>

      <div className="mt-2 flex flex-col gap-2" data-testid="mobile-menu-nav">
        <div className={mutedClass}>Explore</div>
        <Link
          to="/pokemon/en/sets"
          onClick={onClose}
          data-testid="mobile-menu-sets"
          className={linkClass}
        >
          Sets
        </Link>
        <Link
          to="/leaderboard"
          onClick={onClose}
          data-testid="mobile-menu-leaderboard"
          className={linkClass}
        >
          Leaderboard
        </Link>
        {user && (
          <Link
            to="/profile"
            onClick={onClose}
            data-testid="mobile-menu-my-entries"
            className={linkClass}
          >
            My Entries
          </Link>
        )}
        {user?.is_admin && (
          <Link
            to="/admin"
            onClick={onClose}
            data-testid="mobile-menu-admin"
            className={linkClass}
          >
            Admin
          </Link>
        )}
      </div>
    </div>
  );
}
